import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { GatewayTransferPanel } from "@/components/GatewayTransferPanel";
import { GatewayHealthBadge } from "@/components/GatewayHealthBadge";
import { GatewayRelayPanel } from "@/components/GatewayRelayPanel";
import { SharekhanLinkPanel } from "@/components/SharekhanLinkPanel";
import { SharekhanSyncPanel } from "@/components/sync/SharekhanSyncPanel";
import { CertExpiryPanel } from "@/components/CertExpiryPanel";
import { Plug, Lock, Star } from "lucide-react";
import { cn } from "@/lib/utils";

// Configurable broker display ordering. Lower number = higher priority (shown first).
// Add/adjust keys here to re-prioritise brokers in the future — no other code change needed.
// Any broker not listed falls back to DEFAULT_PRIORITY and keeps its original (API) order.
const BROKER_PRIORITY = {
  sharekhan: 0, // Primary broker — first broker selected for Tradify P2.1 integration.
};
const DEFAULT_PRIORITY = 100;
const PRIMARY_BROKER_KEY = "sharekhan";

const orderBrokers = (list) =>
  list
    .map((b, i) => ({ b, i })) // keep original order as a stable tiebreaker
    .sort((x, y) => {
      const px = BROKER_PRIORITY[x.b.key] ?? DEFAULT_PRIORITY;
      const py = BROKER_PRIORITY[y.b.key] ?? DEFAULT_PRIORITY;
      return px - py || x.i - y.i;
    })
    .map((x) => x.b);

export default function Brokers() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["brokers"],
    queryFn: async () => (await api.get("/brokers")).data,
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState label="Failed to load brokers" onRetry={() => refetch()} />;

  const items = orderBrokers(data?.items ?? []);

  return (
    <div>
      <PageHeader title="Broker Connections" subtitle="Sharekhan (stocks) · Binance (crypto) · no live connections" icon={Plug} testId="brokers-header" />

      <div className="flex items-center gap-2 mb-4 rounded-lg border border-slate-600/40 bg-slate-500/10 px-4 py-3">
        <Lock className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-xs text-slate-400">Live broker connections and order execution are disabled. Tradify AI supports exactly two integrations: Sharekhan for stocks and Binance for crypto.</span>
      </div>
      {data?.archived_count > 0 && (
        <div className="mb-4 text-xs text-slate-400" data-testid="brokers-archived-note">
          {data.archived_count} out-of-scope broker records archived (kept for history, disabled).
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="brokers-grid">
        {items.map((b) => {
          const matrix = b.capability_matrix || [];
          const docCount = matrix.filter((m) => m.documented).length;
          const isPrimary = b.key === PRIMARY_BROKER_KEY;
          return (
          <Panel key={b.id} testId={`broker-card-${b.key}`}>
            <div className={cn("p-4", isPrimary && "ring-1 ring-amber-400/30 rounded-[inherit]")}>
              {isPrimary && (
                <div className="flex items-center gap-1 mb-2 text-[9px] font-mono uppercase tracking-wider text-amber-300/90" data-testid={`broker-primary-${b.key}`}>
                  <Star className="w-3 h-3 fill-amber-400/80 text-amber-400/80" /> Primary Broker
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-md bg-[var(--surface)] border border-[var(--border-subtle)] flex items-center justify-center font-display font-bold text-slate-300">{b.name[0]}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-200">{b.name}</div>
                    <div className="text-[10px] text-slate-500">{b.region}</div>
                  </div>
                </div>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded border text-slate-400 border-slate-600/40 uppercase" data-testid={`broker-state-${b.key}`}>
                  {b.connection_state === "not_connected" || !b.connection_state ? "Not Connected" : b.connection_state}
                </span>
              </div>
              {b.read_only && (
                <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 space-y-0.5" data-testid={`broker-roinfo-${b.key}`}>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-amber-300/90 font-mono uppercase">Read-Only Adapter</span>
                    <span className="text-amber-200/80 font-mono">{(b.api_status || "api_not_activated").replace(/_/g, " ")}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>Mode</span><span className="font-mono text-sky-300/90">{(b.integration_mode || "mock_testing").replace(/_/g, " ")}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>Last sync</span><span className="font-mono">{b.last_sync || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>Data freshness</span><span className="font-mono">{b.data_freshness || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>Reconciliation</span><span className="font-mono">{(b.reconciliation_status || "not_run").replace(/_/g, " ")}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mb-3">
                {b.segments.map((s) => <span key={s} className="text-[9px] font-mono text-slate-400 bg-[var(--surface)] border border-[var(--border-subtle)] rounded px-1.5 py-0.5 uppercase">{s}</span>)}
              </div>
              <div className="rounded-md border border-[var(--border-subtle)] overflow-hidden mb-3">
                <div className="grid grid-cols-[1fr_repeat(5,auto)] text-[9px] font-mono bg-[var(--surface)] text-slate-500 px-2 py-1 gap-1">
                  <span>Capability</span><span title="Defined by Tradify adapter">Def</span><span title="Documented by broker">Doc</span><span title="Implemented">Impl</span><span title="Tested">Test</span><span title="Permitted">Perm</span>
                </div>
                <div className="max-h-[132px] overflow-y-auto">
                  {matrix.map((m) => (
                    <div key={m.capability} className="grid grid-cols-[1fr_repeat(5,auto)] text-[9px] px-2 py-0.5 gap-1 border-t border-[var(--border-subtle)]">
                      <span className="text-slate-400 truncate capitalize">{m.capability.replace(/_/g, " ")}</span>
                      {["defined", "documented", "implemented", "tested", "permitted"].map((k) => (
                        <span key={k} className={cn("w-3 text-center", m[k] ? "text-emerald-400" : "text-slate-700")}>{m[k] ? "✓" : "·"}</span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-[10px] text-slate-600">{docCount}/{matrix.length} capabilities documented · 0 implemented in Phase 1</div>
              <button data-testid={`broker-connect-${b.key}`} disabled className="w-full mt-3 h-8 rounded-md border border-[var(--border-subtle)] text-xs text-slate-600 cursor-not-allowed flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Connect (Disabled in Phase 1)
              </button>
            </div>
          </Panel>
          );
        })}
      </div>
      <div className="mt-4"><GatewayHealthBadge /></div>
      <div className="mt-4"><SharekhanSyncPanel /></div>
      <div className="mt-4"><GatewayTransferPanel /></div>
      <div className="mt-4"><GatewayRelayPanel /></div>
      <div className="mt-4"><SharekhanLinkPanel /></div>
      <div className="mt-4"><CertExpiryPanel /></div>
    </div>
  );
}
